export const GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION =
  "cogo.generated-application-viewport.v1" as const;

export const GENERATED_APPLICATION_VIEWPORT_METADATA_KEY =
  "generatedApplicationViewport" as const;

export const GENERATED_APPLICATION_VIEWPORT_MAX_EDGE = 8_192;

export type GeneratedApplicationViewportDevice =
  | "mobile"
  | "tablet"
  | "desktop";

/**
 * Viewport geometry authored with the generated application.
 *
 * This is content metadata, not a measurement of the panel that happens to
 * display it. Every Studio surface therefore sees the same responsive width
 * for the same generated artifact revision.
 */
export interface GeneratedApplicationViewportMetadata {
  schemaVersion: typeof GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION;
  width: number;
  height: number;
  device?: GeneratedApplicationViewportDevice;
}

export const DEFAULT_GENERATED_APPLICATION_VIEWPORT: Readonly<GeneratedApplicationViewportMetadata> =
  Object.freeze({
    schemaVersion: GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION,
    width: 1_280,
    height: 800,
    device: "desktop",
  });

export type GeneratedApplicationViewportFallbackReason = "absent" | "invalid";

export type GeneratedApplicationViewportResolution =
  | {
      authority: "generated-content-metadata";
      fallbackReason: null;
      viewport: GeneratedApplicationViewportMetadata;
    }
  | {
      authority: "safe-default";
      fallbackReason: GeneratedApplicationViewportFallbackReason;
      viewport: GeneratedApplicationViewportMetadata;
    };

/**
 * Presentation geometry keeps generated content layout at its declared
 * viewport and only scales that viewport down when a host is smaller. A large
 * host never enlarges generated content or becomes its responsive authority.
 */
export interface GeneratedApplicationViewportPresentation {
  layoutWidth: number;
  layoutHeight: number;
  displayWidth: number;
  displayHeight: number;
  scale: number;
}

type UnknownRecord = Record<string, unknown>;

function readRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function readDimension(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value > 0 &&
    value <= GENERATED_APPLICATION_VIEWPORT_MAX_EDGE
    ? value
    : null;
}

function inferViewportDevice(width: number): GeneratedApplicationViewportDevice {
  if (width <= 600) return "mobile";
  if (width <= 1_024) return "tablet";
  return "desktop";
}

function readViewportMetadata(
  value: unknown,
): GeneratedApplicationViewportMetadata | null {
  const candidate = readRecord(value);
  if (
    !candidate ||
    candidate.schemaVersion !== GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION
  ) {
    return null;
  }
  const width = readDimension(candidate.width);
  const height = readDimension(candidate.height);
  if (width === null || height === null) return null;
  const declaredDevice = candidate.device;
  if (
    declaredDevice !== undefined &&
    declaredDevice !== "mobile" &&
    declaredDevice !== "tablet" &&
    declaredDevice !== "desktop"
  ) {
    return null;
  }
  return {
    schemaVersion: GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION,
    width,
    height,
    device: declaredDevice ?? inferViewportDevice(width),
  };
}

function copyViewport(
  viewport: GeneratedApplicationViewportMetadata,
): GeneratedApplicationViewportMetadata {
  return { ...viewport };
}

/**
 * Resolve viewport authority from the generated bytes alone.
 *
 * Deliberately no container dimensions are accepted here. Missing or malformed
 * metadata has a visible, deterministic 1280x800 desktop fallback and reports
 * why the fallback was used instead of silently borrowing a host panel size.
 */
export function resolveGeneratedApplicationViewport(
  generatedContent: unknown,
): GeneratedApplicationViewportResolution {
  const content = readRecord(generatedContent);
  const metadata = content ? readRecord(content.metadata) : null;
  const declared = metadata?.[GENERATED_APPLICATION_VIEWPORT_METADATA_KEY];
  const viewport = readViewportMetadata(declared);
  if (viewport) {
    return {
      authority: "generated-content-metadata",
      fallbackReason: null,
      viewport,
    };
  }
  return {
    authority: "safe-default",
    fallbackReason:
      content?.metadata !== undefined && !metadata
        ? "invalid"
        : declared === undefined
          ? "absent"
          : "invalid",
    viewport: copyViewport(DEFAULT_GENERATED_APPLICATION_VIEWPORT),
  };
}

/**
 * Stamp an admitted generated application without mutating it or discarding
 * sibling metadata. Omitting the viewport records the common desktop default
 * explicitly, so Bot, Chat, IDE, and Library do not derive different sizes.
 */
export function withGeneratedApplicationViewport(
  generatedContent: unknown,
  viewport: GeneratedApplicationViewportMetadata =
    copyViewport(DEFAULT_GENERATED_APPLICATION_VIEWPORT),
): UnknownRecord {
  const content = readRecord(generatedContent);
  if (!content) {
    throw new Error("Generated application content must be a plain object.");
  }
  const existingMetadata = content.metadata;
  const metadata =
    existingMetadata === undefined ? {} : readRecord(existingMetadata);
  if (!metadata) {
    throw new Error("Generated application metadata must be a plain object.");
  }
  const admittedViewport = readViewportMetadata(viewport);
  if (!admittedViewport) {
    throw new Error("Generated application viewport metadata is invalid.");
  }
  return {
    ...content,
    metadata: {
      ...metadata,
      [GENERATED_APPLICATION_VIEWPORT_METADATA_KEY]: admittedViewport,
    },
  };
}

/**
 * Fit is presentation-only: the layout viewport remains content-authored.
 * Invalid or unmeasured host geometry leaves the content at 1:1 instead of
 * manufacturing a resize.
 */
export function fitGeneratedApplicationViewport(
  viewport: GeneratedApplicationViewportMetadata,
  host: { width: number; height: number } | null | undefined,
): GeneratedApplicationViewportPresentation {
  const admittedViewport = readViewportMetadata(viewport);
  if (!admittedViewport) {
    throw new Error("Generated application viewport metadata is invalid.");
  }
  const hostWidth = host?.width;
  const hostHeight = host?.height;
  const hostIsMeasured =
    typeof hostWidth === "number" &&
    Number.isFinite(hostWidth) &&
    hostWidth > 0 &&
    typeof hostHeight === "number" &&
    Number.isFinite(hostHeight) &&
    hostHeight > 0;
  const scale = hostIsMeasured
    ? Math.min(
        1,
        hostWidth / admittedViewport.width,
        hostHeight / admittedViewport.height,
      )
    : 1;
  return {
    layoutWidth: admittedViewport.width,
    layoutHeight: admittedViewport.height,
    displayWidth: admittedViewport.width * scale,
    displayHeight: admittedViewport.height * scale,
    scale,
  };
}

/** Build a complete declaration while keeping device inference in one place. */
export function generatedApplicationViewportMetadata(input: {
  width: number;
  height: number;
  device?: GeneratedApplicationViewportDevice;
}): GeneratedApplicationViewportMetadata {
  const viewport = readViewportMetadata({
    schemaVersion: GENERATED_APPLICATION_VIEWPORT_SCHEMA_VERSION,
    width: input.width,
    height: input.height,
    device: input.device,
  });
  if (!viewport) {
    throw new Error("Generated application viewport metadata is invalid.");
  }
  return viewport;
}
